<template lang="pug">
.header
    h3.title {{ $t('settings_tab.campaign_tab.manage.title') }}
      aiq-tooltip(:content="$t('settings_tab.campaign_tab.manage.campaign_tooltip')"
        placement="top")
        fa-icon.question-mark(:icon="['fas', 'circle-question']")
    .add-section(v-if="canView('/conversations/right_panel/manage_campaign') && !executed")
      aiq-tooltip(effect="dark",
             :content="$t('settings_tab.campaign_tab.edit.title')",
             placement="bottom",
             :show-after="500")
        i.el-icon-plus(@click="addCampaign()" class="add-icon")
.body-section
    .list(v-if="!showEditor")
      .campaign-list
        .group-item(v-for="(group, key) in grouped" :key="key")
          aiq-collapse
            aiq-collapse-item(:title="keyTitle(key, group)")
              template(v-slot:title="{ isActive }")
                aiq-tooltip(effect="dark", placement="bottom", :content="keyTitle(key, group, true)", :show-after="500")
                  .title-wrapper(:class="['title-wrapper', { 'is-active': isActive }]") {{ keyTitle(key, group) }}
              .campaign-item(v-for="item in group" :class="item.executed ? 'executed' : ''" :key="item.id")
                .campaign-date {{parseDate(item.run_at)}} {{ getExecutedState(item) }}
                .campaign-failure(v-if="shouldShowFailureReason(item)") {{ getFailureReason(item) }}
                ul
                  li {{parseAction(item, 'action')}}
                  li {{parseAction(item, 'template')}}
                  li {{parseAction(item, 'Message Type')}}
                  li {{parseSendTime(item)}}
              .delete-btn
                aiq-button(v-if="canView('/conversations/right_panel/manage_campaign') && !executed" type="danger" :plain="true" @click="deleteCampaign(group)") {{ $t('global.delete') }}
CampaignAdd(v-if="showEditor"
                :CampaignList="CampaignList"
                :addInProgress="addInProgress"
                @save="addCustomerCampaign"
                @cancel="showEditor = false")
</template>
<script>
import CampaignAdd from './Add.vue';
import capitalize from 'lodash/capitalize';
import dayjs from '@/localPlugins/dayjs';
import get from 'lodash/get';
import { useTimeZone, getTimeZoneAbbr, getMetricsTimeZoneAbbr } from '@/libs';

const STATUS = {
  'pending': 'Pending',
  'executed': 'Sent',
  'failed': 'Failed',
};

export default {
  name: 'CustomerCampaign',
  components: {
    CampaignAdd,
  },
  props: {
    executed: {
      type: Boolean,
      default: false,
    },
    grouped: {
      type: Object,
      default: () => {},
    },
    CampaignList: {
      type: Array,
      default: () => [],
    },
    CustomerList: {
      type: Array,
      default: () => [],
    },
    customer: {
      type: Object,
      default: () => {},
    },
    allCustomerCampaigns: {
      type: Array,
      default: () => {},
    },
  },
  data() {
    return {
      showEditor: false,
      addInProgress: false,
      timezone: '',
      timezoneAbbr: '',
    };
  },
  async mounted() {
    this.timezone = await useTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      this.$store.dispatch,
      this.$store._state.data.configs,
    );

    this.timezoneAbbr = getTimeZoneAbbr(this.timezone);
  },
  emits: ['refresh'],
  methods: {
    /**
     * Generate a title for a campaign group based on the key and earliest date in the group
     * @param {string} key - the key of the group
     * @param {array} group - the array of campaigns in the group
     * @param {boolean} [showFull=false] - whether to show the full key or just a truncated version
     * @returns {string} a title for the campaign group
     */
    keyTitle(key, group, showFull = false) {
      const earliestDate = group.reduce((prev, curr) => {
        return prev.run_at < curr.run_at ? prev : curr;
      });
      const maxLength = 18;
      if (key.length > maxLength && !showFull) {
        return `${key.substring(0, maxLength)}... (Starts ${dayjs(earliestDate.run_at).format('MM/DD/YYYY')})`;
      }
      //get the earliest duration
      const earliestIntervals = get(earliestDate, 'campaign.intervals.items', []);
      //get the item with the least duration
      const duration = earliestIntervals.reduce((prev, curr) => {
        return prev.duration < curr.duration ? prev : curr;
      });

      //subtract the duration from the earliest date which would give us the selected date
      //to display
      const runAt = dayjs(earliestDate.run_at).subtract(duration.duration, 'days').format('MM/DD/YYYY');
      return `${key} (Starts ${runAt})`;
    },
    getExecutedState(item) {
      const state = get(item, 'campaign_logs[0].payload.status', 'pending');
      return `(${STATUS[state]})`;
    },
    shouldShowFailureReason(item) {
      const state = get(item, 'campaign_logs[0].payload.status', '');
      return state === 'failed';
    },
    getFailureReason(item) {
      const reason = get(item, 'campaign_logs[0].payload.reason', '');
      return reason;
    },
    async deleteConfirm() {
      try {
        await this.$aiq.confirm(
          this.$t('settings_tab.campaign_tab.edit.delete_confirm.title'),
          this.$t('settings_tab.campaign_tab.edit.delete_confirm.message'),
        );
        return true;
      } catch {
        return false;
      }
    },
    async deleteCampaign(group) {
      const response = await this.deleteConfirm();

      try {
        if (response) {
          await this.$store.dispatch('campaign/deleteCustomerCampaign', {
            customerId: this.customer.id,
            campaignId: group[0].campaign_id,
          });
          this.$emit('refresh');
          this.$aiq.notify.success('Campaign deleted successfully');
        }
      } catch (error) {
        this.$aiq.notify.error('Error deleting campaign');
      }
    },
    addCampaign() {
      this.showEditor = true;
    },
    parseAction(item, key) {
      if (key === 'Message Type' || key === 'template') {
        const data = get(item, `metadata.data.${key}`, '');
        return key === 'template' ? `Template: ${data}` : `Message Type: ${data}`;
      }
      const data = get(item, `metadata.data.${key}`, '');
      return `${capitalize(key)}: ${capitalize(data)}`;
    },

    parseDate(date) {
      return dayjs(date).format('MM/DD/YYYY');
    },
    parseSendTime(item) {
      const date = get(item, 'metadata.data.send_at', '');
      const generalTzAbbr = getMetricsTimeZoneAbbr(this.timezone);
      return `Send Time: ${dayjs(date).tz(this.timezone).format('hh:mm a')} (${generalTzAbbr})`;
    },
    async confirmCampaignExist(value) {
      const executed = get(this.allCustomerCampaigns, 'executed', []);
      const pending = get(this.allCustomerCampaigns, 'pending', []);

      try {
        // Check if campaign already exists in executed
        const campaignInExecuted = executed.find((item) => item.campaign.id === value);
        if (campaignInExecuted) {
          await this.$aiq.confirm(
            this.$t('settings_tab.campaign_tab.manage.duplicate'),
            this.$t('settings_tab.campaign_tab.manage.campaign_exist_in_executed', {completion_date: dayjs(campaignInExecuted.run_at).format('MM/DD/YYYY')}),
          );
        }

        // Check if campaign already exists in pending
        const campaignInPending = pending.find((item) => item.campaign.id === value);
        if (campaignInPending) {
          await this.$aiq.confirm(
            this.$t('settings_tab.campaign_tab.manage.duplicate'),
            this.$t('settings_tab.campaign_tab.manage.campaign_exist_in_in_progress'),
          );

        }
        return true;

      } catch {
        return false;
      }
    },
    async addCustomerCampaign(campaign) {
      if (!campaign.value || !campaign.date) {
        this.$aiq.notify.error(this.$t('settings_tab.campaign_tab.edit.validation.all_fields_required'));
        return;
      }
      const payload = {
        customer_id: this.customer.id,
        campaign_id: campaign.value,
        start_time: campaign.date,
        payload: {},
      };
      const confirmation = await this.confirmCampaignExist(campaign.value);
      try {
        if (confirmation) {
          this.addInProgress = true;
          await this.$store.dispatch('campaign/createCustomerCampaign', payload);
          this.$emit('refresh');
          this.showEditor = false;
          this.$aiq.notify.success('Campaign created successfully');
        }
      } catch (error) {
        this.$aiq.notify.error('Error creating campaign');
      } finally {
        this.addInProgress = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/styles/aiq-variables.scss';
.question-mark {
  padding-left: 5px;
  cursor: pointer;
}
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.campaign-date {
  font-weight: bold;
  margin-bottom: 5px;
  text-decoration: underline;
}

.campaign-failure {
  font-size: 10px;
  color: $aiq-color-error
}

.campaign-item {
  border: 1px solid var(--aiq-table-border);
  padding: 10px;
  margin-bottom: 10px;
  background-color: $aiq-table-border;
}

.executed {
  background-color: darken($aiq-table-border, 15%);
}

.campaign-list {
  padding-top: 10px;
}
.delete-btn {
  float: right;
  padding-bottom: 10px;
}
:deep(.el-collapse .el-collapse-item__header) {
 background-color: var(--aiq-bgc-list-item-selected) !important;
 color: var(--aiq-bgc-list-item) !important;
 word-wrap: break-word !important;
 height: 40px;
}

:deep(.el-collapse .el-collapse-item__header:hover) {
  background-color: var(--aiq-bgc-list-item-selected) !important;
  color: var(--aiq-bgc-list-item) !important;
  word-wrap: break-word !important;
  height: 40px;
}
:deep(.el-collapse .el-collapse-item__header .el-collapse-item__arrow) {
  color: var(--aiq-bgc-list-item) !important;
}
:deep(.el-collapse-item__header) {
  line-height: 20px;
  font-size: 12px;
}
</style>